import React, { useCallback, useMemo } from "react";

import { faInfoCircle, faRobot } from "@fortawesome/pro-regular-svg-icons";
import { subMinutes } from "date-fns/subMinutes";
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { CopilotButton } from "src/base-components/CopilotButton";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { Select } from "src/base-components/Select";
import { PreviewQueryRequestEnvironmentEnum } from "src/clients/features-control";
import { useFeatureQueryCopilot } from "src/copilot/useFeatureQueryCopilot";
import { Accordion, useAccordionSingleMode } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { PreviewData } from "src/featureCatalogue/Queries/Create/helpers";
import { RunQueryButton } from "src/featureCatalogue/Queries/Create/RunQueryButton";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import {
  getCompletionExtension,
  getWindowHopOptions,
  getWindowLengthOptions,
  isWindowHopOptionDisabled,
} from "src/featureCatalogue/Queries/Create/utils";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";
import { logger } from "src/utils/logger";

type QuerySectionProps = {
  readOnly: boolean;
  featureQueryPreview: UseFeatureQueryPreview;
  handleFeatureGeneration?: (previewData: PreviewData) => void;
};

export const SectionHeader: React.FC<{
  title: string;
  description?: string;
  tooltip?: React.ReactNode;
}> = ({ title, description, tooltip }) => {
  return (
    <div className="pb-2">
      <h3
        className={twJoin(
          "font-inter-semibold-13px text-gray-800",
          description && "pb-2",
        )}
      >
        <span>{title}</span>
        {tooltip && (
          <Tooltip body={tooltip}>
            <Icon icon={faInfoCircle} size="2xs" variant="hover" />
          </Tooltip>
        )}
      </h3>
      {description && (
        <p className="font-inter-normal-12px text-gray-500">{description}</p>
      )}
    </div>
  );
};

export const QuerySection: React.FC<QuerySectionProps> = ({
  readOnly,
  featureQueryPreview,
  handleFeatureGeneration,
}) => {
  const form = useFormContext<QueryFormValues>();
  const { value, onChange } = useAccordionSingleMode("sql-query");

  const { openFeatureQueryCopilot: openCopilot } = useFeatureQueryCopilot();

  const windowHop = form.watch("window_hop");
  const windowLength = form.watch("window_length");

  const handleRunQueryShortcut = useCallback(async () => {
    const [queryString, windowLength, windowHop, endTime] = form.getValues([
      "query",
      "window_length",
      "window_hop",
      "end_datetime",
    ]);

    if (
      !queryString?.length ||
      !windowLength ||
      !windowHop ||
      featureQueryPreview.isPending
    ) {
      return false;
    }

    try {
      await featureQueryPreview.triggerPreview({
        queryString,
        windowLength,
        environment: PreviewQueryRequestEnvironmentEnum.SANDBOX,
        endTime,
      });

      const previewData = await featureQueryPreview.promise.current?.promise;

      if (previewData?.data && handleFeatureGeneration) {
        handleFeatureGeneration(previewData.data);
      }
    } catch (error) {
      logger.error(error);
    }

    return true;
  }, [form, featureQueryPreview, handleFeatureGeneration]);

  const keyBindings = useMemo(
    () => [
      {
        mac: "Cmd-Enter",
        win: "Ctrl-Enter",
        linux: "Ctrl-Enter",
        run: () => {
          const [queryString, windowLength, windowHop] = form.getValues([
            "query",
            "window_length",
            "window_hop",
          ]);

          if (
            !queryString?.length ||
            !windowLength ||
            !windowHop ||
            featureQueryPreview.isPending
          ) {
            return false;
          }

          handleRunQueryShortcut();
          return true;
        },
      },
    ],
    [handleRunQueryShortcut, form, featureQueryPreview],
  );

  const handleOpenCopilot = useCallback(() => {
    openCopilot({
      getCurrentQuery: () => form.getValues("query"),
      getLastQueryErrorMessage: () =>
        form.formState.errors.query?.message || "",
      onApplyQuery: (suggestion) => {
        form.setValue("query", suggestion.query);
        form.trigger("query");
      },
    });
  }, [openCopilot, form]);

  const refreshIntervalOptions = useMemo(() => {
    return getWindowHopOptions().map((option) => ({
      ...option,
      disabled: isWindowHopOptionDisabled(parseInt(option.key), windowLength)
        ? "This option is not available for the selected time window"
        : false,
    }));
  }, [windowLength]);

  const onChangeWindowLength = useCallback(
    (
      value: string,
      field: ControllerRenderProps<QueryFormValues, "window_length">,
    ) => {
      const parsedWindowLengthValue = parseInt(value);

      if (isWindowHopOptionDisabled(windowHop, parsedWindowLengthValue)) {
        form.resetField("window_hop", {
          // @ts-expect-error - we want to reset the field to null
          defaultValue: null,
        });
      }
      return field.onChange(parsedWindowLengthValue);
    },
    [form, windowHop],
  );

  return (
    <>
      <Accordion value={value} onChange={onChange}>
        <Accordion.Item
          dataLoc="sql-query-accordion"
          rightSlot={
            <CopilotButton
              disabled={readOnly}
              icon={faRobot}
              onClick={handleOpenCopilot}
            >
              Ask Copilot
            </CopilotButton>
          }
          title="SQL Query"
          value="sql-query"
        >
          <div className="h-80 grow pb-6">
            <Controller
              name="query"
              render={({ field, fieldState }) => (
                <CodeEditor
                  completionExtension={getCompletionExtension(readOnly)}
                  dataLoc="sql-query-editor"
                  disabled={readOnly}
                  errorHighlightedLine={
                    fieldState.error?.message ? 1 : undefined
                  }
                  keyBindings={keyBindings}
                  language="sql"
                  value={field.value}
                  onBlur={() => form.formState.isSubmitted && form.trigger()}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          {/* Setup timing and Run Query Section */}
          <div>
            <div className="flex items-center gap-x-4 pb-4">
              <div className="flex-1">
                <SectionHeader
                  title="Time window"
                  tooltip="The time window for calculating your feature (e.g., last 30 days of transactions). Maximum 365 days."
                />
                <Controller
                  control={form.control}
                  name="window_length"
                  render={({ field }) => (
                    <Select
                      dataLoc="window-length-select"
                      disabled={readOnly}
                      errored={!!form.formState.errors.window_length}
                      options={getWindowLengthOptions()}
                      placeholder="Select time window"
                      value={field.value ? field.value.toString() : null}
                      searchable
                      onChange={(value) => onChangeWindowLength(value, field)}
                    />
                  )}
                />
              </div>
              <div className="flex-1">
                <SectionHeader title="End date and time for preview" />
                <Controller
                  control={form.control}
                  defaultValue={subMinutes(new Date(), 1)}
                  name="end_datetime"
                  render={({ field }) => (
                    <DateTimePickerDropdown
                      disabled={readOnly}
                      errored={!!form.formState.errors.end_datetime}
                      placeholder="Select end date and time"
                      value={field.value as Date | undefined}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
            <div className="pb-6">
              <SectionHeader
                title="Refresh interval"
                tooltip="How often your feature values are updated. Shorter intervals mean more recent data. Match to your decision-making needs (e.g., 1 min for real-time fraud, 1 day for periodic reviews)"
              />
              <Controller
                control={form.control}
                name="window_hop"
                render={({ field }) => {
                  return (
                    <Select
                      dataLoc="window-hop-select"
                      disabled={readOnly}
                      errored={!!form.formState.errors.window_hop}
                      options={refreshIntervalOptions}
                      placeholder="Select refresh interval"
                      value={field.value ? field.value.toString() : null}
                      searchable
                      onChange={(value) => field.onChange(parseInt(value))}
                    />
                  );
                }}
              />
            </div>
            <div className="flex items-start gap-x-2">
              <RunQueryButton
                disabled={readOnly || !windowLength || !windowHop}
                featurePreview={featureQueryPreview}
                handleFeatureGeneration={handleFeatureGeneration}
              />
            </div>
            <QueryHasChangedCallout previewMutation={featureQueryPreview} />
          </div>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

const QueryHasChangedCallout: React.FC<{
  previewMutation: UseFeatureQueryPreview;
}> = ({ previewMutation }) => {
  const { control } = useFormContext<QueryFormValues>();
  const [queryString, windowLength] = useWatch({
    control,
    name: ["query", "window_length"],
  });

  const hasQueryChanged =
    previewMutation.variables?.queryString !== queryString;
  const hasWindowLengthChanged =
    previewMutation.variables?.windowLength !== windowLength;

  const previewColumns = Object.keys(
    previewMutation.previewData?.data?.at(0) ?? {},
  );

  // We need to have at least 2 columns to create a feature: one for the entity and one for the feature value.
  const previewSettled =
    previewMutation.isSuccess && !previewMutation.isPending;
  const hasTooFewColumns =
    previewSettled &&
    !hasQueryChanged &&
    !hasWindowLengthChanged &&
    previewColumns.length < 2;

  if (previewSettled && (hasQueryChanged || hasWindowLengthChanged)) {
    return (
      <div className="pt-4">
        <Callout type="warning">
          {hasQueryChanged
            ? "The query has changed, please run the query again"
            : "The time window has changed, please run the query again"}
        </Callout>
      </div>
    );
  }

  if (hasTooFewColumns) {
    return (
      <div className="pt-4">
        <Callout type="error">
          The query must return at least 2 columns: one for the entity and one
          for a feature.
        </Callout>
      </div>
    );
  }

  return null;
};
